import React, {useEffect, useMemo, useState} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {Delete, Edit, Save} from "@mui/icons-material";
import {format, parseISO} from "date-fns";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";
import {fetchMarketPriceAreas, MarketPriceArea} from "../../api/marketPrice";
import {
  createSeedAvailability,
  deleteSeedAvailability,
  fetchSeedAvailabilities,
  SeedAvailabilityRow,
  updateSeedAvailability,
} from "../../api/seedAvailability";

const formatShortDate = (isoDate: string): string => {
  const raw = (isoDate || "").slice(0, 10);
  try {
    return format(parseISO(raw), "d-MMM");
  } catch {
    return raw;
  }
};

const toInputDate = (isoDate: string): string => (isoDate || "").slice(0, 10);

const SeedAvailabilityPage: React.FC = () => {
  const {user} = useAuth();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [areas, setAreas] = useState<MarketPriceArea[]>([]);
  const [rows, setRows] = useState<SeedAvailabilityRow[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState("");

  const [form, setForm] = useState({
    id: "",
    hatcheryName: "",
    location: "",
    broodstock: "",
    quantity: "",
    price: "",
    availabilityDate: "",
    contact: "",
    isActive: true,
    isPublic: false,
  });

  const activeAreas = useMemo(() => areas.filter((a) => a.isActive), [areas]);

  const canSubmit =
    !!selectedAreaId &&
    !!form.hatcheryName.trim() &&
    !!form.location.trim() &&
    !!form.broodstock.trim() &&
    !!form.quantity.trim() &&
    !!form.price &&
    !!form.availabilityDate &&
    !!form.contact.trim();

  const loadAreas = async () => {
    setLoading(true);
    setError("");
    try {
      const areasData = await fetchMarketPriceAreas(!isSuperAdmin);
      setAreas(areasData);
      setSelectedAreaId((prev) => prev || areasData.find((a) => a.isActive)?.id || "");
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load areas");
    } finally {
      setLoading(false);
    }
  };

  const loadRows = async () => {
    if (!selectedAreaId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await fetchSeedAvailabilities({
        areaId: selectedAreaId,
        activeOnly: !isSuperAdmin,
      });
      setRows(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load seed availability");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAreas();
  }, [isSuperAdmin]);

  useEffect(() => {
    loadRows();
  }, [selectedAreaId, isSuperAdmin]);

  const resetForm = () =>
    setForm({
      id: "",
      hatcheryName: "",
      location: "",
      broodstock: "",
      quantity: "",
      price: "",
      availabilityDate: "",
      contact: "",
      isActive: true,
      isPublic: false,
    });

  const saveRow = async () => {
    if (!selectedAreaId || !canSubmit) return;
    try {
      const payload = {
        areaId: selectedAreaId,
        hatcheryName: form.hatcheryName.trim(),
        location: form.location.trim(),
        broodstock: form.broodstock.trim(),
        quantity: form.quantity.trim(),
        price: Number(form.price),
        availabilityDate: form.availabilityDate,
        contact: form.contact.trim(),
        isActive: form.isActive,
        isPublic: form.isPublic,
      };
      if (form.id) {
        await updateSeedAvailability(form.id, payload);
      } else {
        await createSeedAvailability(payload);
      }
      resetForm();
      await loadRows();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to save seed availability");
    }
  };

  const editRow = (row: SeedAvailabilityRow) => {
    setForm({
      id: row.id,
      hatcheryName: row.hatcheryName,
      location: row.location,
      broodstock: row.broodstock,
      quantity: row.quantity,
      price: String(row.price),
      availabilityDate: toInputDate(row.availabilityDate),
      contact: row.contact,
      isActive: row.isActive,
      isPublic: !!row.isPublic,
    });
  };

  const selectedAreaName = activeAreas.find((a) => a.id === selectedAreaId)?.name || "-";

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h4" gutterBottom>
        Seed Availability
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
        Hatchery seed listings by region. Select an area to view details.
      </Typography>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      <Paper sx={{p: 2, mb: 3}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select Area</InputLabel>
              <Select label="Select Area" value={selectedAreaId} onChange={(e) => setSelectedAreaId(e.target.value)}>
                {activeAreas.map((area) => (
                  <MenuItem key={area.id} value={area.id}>
                    {area.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Card sx={{mb: 3}}>
        <CardContent>
          <Box sx={{display: "flex", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1}}>
            <Typography variant="h6">Area: {selectedAreaName}</Typography>
            <Chip color="primary" label="Shrimp seed" />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hatchery</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Broodstock</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Contact</TableCell>
                  {isSuperAdmin && <TableCell>Status</TableCell>}
                  {isSuperAdmin && <TableCell>Public</TableCell>}
                  {isSuperAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 10 : 7} align="center">
                      {loading ? "Loading..." : "No seed availability configured"}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.hatcheryName}</TableCell>
                    <TableCell>{row.location}</TableCell>
                    <TableCell>{row.broodstock}</TableCell>
                    <TableCell>{row.quantity}</TableCell>
                    <TableCell>₹{Number(row.price).toFixed(2)}</TableCell>
                    <TableCell>{formatShortDate(row.availabilityDate)}</TableCell>
                    <TableCell>{row.contact}</TableCell>
                    {isSuperAdmin && (
                      <TableCell>
                        <Chip size="small" label={row.isActive ? "Active" : "Inactive"} color={row.isActive ? "success" : "default"} />
                      </TableCell>
                    )}
                    {isSuperAdmin && (
                      <TableCell>
                        <Chip size="small" label={row.isPublic ? "Yes" : "No"} color={row.isPublic ? "primary" : "default"} />
                      </TableCell>
                    )}
                    {isSuperAdmin && (
                      <TableCell align="right">
                        <Button size="small" startIcon={<Edit />} onClick={() => editRow(row)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={async () => {
                            await deleteSeedAvailability(row.id);
                            await loadRows();
                          }}>
                          Delete
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info" sx={{mt: 2}}>
            Availability and pricing can change frequently. Confirm details with the hatchery before planning stocking.
          </Alert>
        </CardContent>
      </Card>

      {isSuperAdmin && (
        <Stack spacing={2}>
          <Divider />
          <Typography variant="h5">Super Admin — Seed rows</Typography>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hatchery"
                    value={form.hatcheryName}
                    onChange={(e) => setForm({...form, hatcheryName: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={form.location}
                    onChange={(e) => setForm({...form, location: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Broodstock"
                    value={form.broodstock}
                    onChange={(e) => setForm({...form, broodstock: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Quantity (e.g. 25L)"
                    value={form.quantity}
                    onChange={(e) => setForm({...form, quantity: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{step: "0.01", min: 0}}
                    label="Price (₹)"
                    value={form.price}
                    onChange={(e) => setForm({...form, price: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Availability date"
                    InputLabelProps={{shrink: true}}
                    value={form.availabilityDate}
                    onChange={(e) => setForm({...form, availabilityDate: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Contact"
                    value={form.contact}
                    onChange={(e) => setForm({...form, contact: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{display: "flex", alignItems: "center"}}>
                  <FormControlLabel
                    control={<Checkbox checked={form.isActive} onChange={(e) => setForm({...form, isActive: e.target.checked})} />}
                    label="Active"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={form.isPublic} onChange={(e) => setForm({...form, isPublic: e.target.checked})} />}
                    label="Public (login teaser)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" startIcon={<Save />} onClick={saveRow} disabled={!canSubmit}>
                      {form.id ? "Update row" : "Add row"}
                    </Button>
                    {form.id && (
                      <Button variant="outlined" onClick={resetForm}>
                        Cancel
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};

export default SeedAvailabilityPage;
